import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { CreateWorkScheduleDtoTypeEnum, WorkScheduleTypeEnum } from '../../../../apis/client-axios';
import './style.scss';

interface DayInfo {
  date: number | null;
  type: string | null;
}

const DayName = ({ name }) => {
  return <div className="calendar-container__day-name">{name}</div>;
};

const Day = ({ date, type, isInMonth, onDayClick }) => {
  const [currentType, setCurrentType] = useState(type);

  useEffect(() => {
    setCurrentType(type);
  }, [type]);

  const handleDayClick = () => {
    if (!isInMonth) return;

    const nextType = {
      WORKING_DAYS: WorkScheduleTypeEnum.LegalDaysOff,
      LEGAL_DAYS_OFF: WorkScheduleTypeEnum.RegulatedDaysOff,
      REGULATED_DAYS_OFF: WorkScheduleTypeEnum.WorkingDays,
    };

    setCurrentType((prevType) => {
      const newType = nextType[prevType] || WorkScheduleTypeEnum.WorkingDays;
      onDayClick(date, newType);
      return newType;
    });
  };

  const getBackgroundColor = () => {
    if (!date || !isInMonth) {
      return 'calendar-container__noday';
    }

    switch (currentType) {
      case WorkScheduleTypeEnum.WorkingDays:
        return 'calendar-container__normal';
      case WorkScheduleTypeEnum.LegalDaysOff:
        return 'calendar-container__statutory-rest-days';
      case WorkScheduleTypeEnum.RegulatedDaysOff:
        return 'calendar-container__scheduled-holidays';
      default:
        return 'calendar-container__normal';
    }
  };

  return (
    <div className={`calendar-container__day ${getBackgroundColor()}`} onClick={handleDayClick}>
      {date || ''}
    </div>
  );
};

const MonthDisplay = ({ month }) => {
  const intl = useIntl();
  const monthNames = [
    intl.formatMessage({ id: 'workinghour.january' }),
    intl.formatMessage({ id: 'workinghour.february' }),
    intl.formatMessage({ id: 'workinghour.march' }),
    intl.formatMessage({ id: 'workinghour.april' }),
    intl.formatMessage({ id: 'workinghour.may' }),
    intl.formatMessage({ id: 'workinghour.june' }),
    intl.formatMessage({ id: 'workinghour.july' }),
    intl.formatMessage({ id: 'workinghour.august' }),
    intl.formatMessage({ id: 'workinghour.september' }),
    intl.formatMessage({ id: 'workinghour.october' }),
    intl.formatMessage({ id: 'workinghour.november' }),
    intl.formatMessage({ id: 'workinghour.december' }),
  ];

  return <div className="calendar-container__month-display">{monthNames[month]}</div>;
};

const CalendarCustom = ({ year, month, data, isLoading, onDayClickProp }) => {
  const adjustedMonth = month - 1;
  const intl = useIntl();

  const dayNames = [
    intl.formatMessage({ id: 'workinghour.sunday' }),
    intl.formatMessage({ id: 'workinghour.monday' }),
    intl.formatMessage({ id: 'workinghour.tuesday' }),
    intl.formatMessage({ id: 'workinghour.wednesday' }),
    intl.formatMessage({ id: 'workinghour.thursday' }),
    intl.formatMessage({ id: 'workinghour.friday' }),
    intl.formatMessage({ id: 'workinghour.saturday' }),
  ];

  const dateTypeMap =
    data?.data?.data?.reduce((acc, day) => {
      acc[day.date] = day.type;
      return acc;
    }, {}) || {};

  const getDaysInMonth = (year, month) => {
    const date = new Date(Date.UTC(year, month, 1));
    const days: DayInfo[] = [];
    const firstDay = date.getDay(); // Sunday = 0
    for (let i = 0; i < firstDay; i++) {
      days.push({ date: null, type: null });
    }

    while (date.getMonth() === month) {
      const dateStr = new Date(Date.UTC(year, month, date.getDate())).toISOString().split('T')[0];
      days.push({
        date: date.getDate(),
        type: dateTypeMap[dateStr] || null,
      });
      date.setDate(date.getDate() + 1);
    }

    const remainingSlots = 7 - (days.length % 7);
    if (remainingSlots < 7) {
      for (let i = 0; i < remainingSlots; i++) {
        days.push({ date: null, type: null });
      }
    }

    return days;
  };

  const days = getDaysInMonth(year, adjustedMonth);

  const handleDayClick = (date: number, type: CreateWorkScheduleDtoTypeEnum) => {
    const formattedDate = new Date(Date.UTC(year, adjustedMonth, date)).toISOString().split('T')[0];
    if (onDayClickProp) {
      onDayClickProp(formattedDate, type);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 text-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <>
        <MonthDisplay month={adjustedMonth} />
        {dayNames.map((name, index) => (
          <DayName key={index} name={name} />
        ))}
        {days.map((day, index) => (
          <Day
            key={index}
            date={day.date || ''}
            type={day.type}
            isInMonth={day.date !== null}
            onDayClick={handleDayClick}
          />
        ))}
      </>
    </div>
  );
};

export default CalendarCustom;
